import { useEffect, useState } from 'react';
import { authApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Copy, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Shows a freshly issued recovery code exactly once.
 *
 * The code is the only way back into an account after a forgotten password
 * (the private key is sealed under it), so the dialog can't be dismissed
 * until the user confirms they've saved it. "Download PDF" fetches a
 * printable sheet from the backend.
 */
export const RecoveryCodeDialog = ({ open, code, onDone, title, intro }) => {
  const [saved, setSaved] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) setSaved(false);
  }, [open, code]);

  if (!code) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Recovery code copied');
    } catch {
      toast.error('Could not copy. Write it down or download the PDF instead.');
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await authApi.recoveryCodePdf(code);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'precious-data-recovery-code.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded. Print it or keep it somewhere safe.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create the PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* must confirm to close */ }}>
      <DialogContent className="sm:max-w-lg [&>button]:hidden" data-testid="recovery-code-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[24px] font-semibold tracking-[-0.02em]">
            {title || 'Save your recovery code'}
          </DialogTitle>
          <DialogDescription className="text-ink-70 text-base">
            {intro ||
              'Your records are encrypted so that only you can read them. If you ever forget your password, this code is the only way to get back in and keep your documents. We cannot recover them for you.'}
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-lg bg-ground px-4 py-5 text-center"
          data-testid="recovery-code-value"
        >
          <p className="pd-mono text-ink-55 mb-2">Recovery code</p>
          <p className="font-mono text-lg sm:text-xl font-semibold tracking-wider break-all select-all text-ink">
            {code}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            onClick={downloadPdf}
            disabled={downloading}
            className="flex-1"
            data-testid="recovery-code-download-pdf"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PDF to save or print
          </Button>
          <Button type="button" variant="secondary" onClick={copy} data-testid="recovery-code-copy">
            <Copy className="w-4 h-4" />
            Copy
          </Button>
        </div>

        <p className="p-4 rounded-lg bg-amber-wash text-amber-ink text-sm leading-relaxed">
          Keep it separate from your password. Anyone with this code and access to your email could
          reset your password. You can make a new one at any time from Profile → Security, which
          stops this one working.
        </p>

        <label className="flex items-start gap-3 cursor-pointer select-none">
          <Checkbox
            checked={saved}
            onCheckedChange={(v) => setSaved(Boolean(v))}
            className="mt-0.5"
            data-testid="recovery-code-saved-checkbox"
          />
          <span className="text-[15px] text-ink">
            I have saved my recovery code somewhere safe. I understand it will not be shown again.
          </span>
        </label>

        <DialogFooter>
          <Button
            type="button"
            disabled={!saved}
            onClick={onDone}
            data-testid="recovery-code-done"
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Rendered once at the app root. Shows whichever recovery code the auth
 * context is holding (from registration or account recovery) regardless of
 * which page the route guards redirect to in the meantime.
 */
export const PendingRecoveryCodeDialog = () => {
  const { pendingRecoveryCode, clearPendingRecoveryCode } = useAuth();
  if (!pendingRecoveryCode?.code) return null;
  const recovered = pendingRecoveryCode.context === 'recover';
  return (
    <RecoveryCodeDialog
      open
      code={pendingRecoveryCode.code}
      title={recovered ? 'Save your new recovery code' : 'Save your recovery code'}
      intro={recovered
        ? 'Your password has been changed and your documents are intact. This is your new recovery code; the one you just used no longer works.'
        : undefined}
      onDone={clearPendingRecoveryCode}
    />
  );
};

export default RecoveryCodeDialog;
